// Pure view logic for the draft room.
//
// Everything here used to live inline in pages/Draft.tsx, where none of it
// could be asserted on without a DOM. The clock arithmetic in particular was
// duplicated between ClockDisplay and the control panel's OnClock with subtly
// different output; this is the single copy.

import { displayPosition } from '../components/board/positions'
import type { PickState, PlayerSummary } from '../types/protocol'

/** "1:23", "0:07". Null deadline reads as a dash, never an empty string. */
export function formatClock(remainingMs: number | null): string {
  if (remainingMs == null) return '--:--'
  const total = Math.ceil(remainingMs / 1000)
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export type ClockTone = 'normal' | 'warn' | 'crit'

/**
 * The three states the clock reads in. Thresholds carried over verbatim from
 * the old ClockDisplay: amber from 30s, rose from 10s. Anything expired or
 * past its deadline is critical regardless of the number shown.
 */
export function clockTone(remainingMs: number | null, expired = false): ClockTone {
  if (expired) return 'crit'
  if (remainingMs == null) return 'normal'
  const total = Math.ceil(remainingMs / 1000)
  if (total <= 10) return 'crit'
  if (total <= 30) return 'warn'
  return 'normal'
}

/**
 * Fraction of the pick clock still left, 0..1, for the drain track under the
 * header. A pick_seconds of 0 (or a missing deadline) reads as full rather
 * than dividing by zero.
 */
export function clockFraction(remainingMs: number | null, pickSeconds: number): number {
  if (remainingMs == null || pickSeconds <= 0) return 1
  return Math.min(1, Math.max(0, remainingMs / (pickSeconds * 1000)))
}

/** Picks still to come, current one first, in draft order. */
export function upcomingPicks(picks: PickState[], currentOverall: number | null): PickState[] {
  if (currentOverall == null) return []
  return picks
    .filter((pick) => pick.overall >= currentOverall && pick.status === 'pending')
    .sort((a, b) => a.overall - b.overall)
}

export interface NextPick {
  pick: PickState
  /** 0 when the team is on the clock right now. */
  picksAway: number
}

/**
 * The team's own next turn. Returns picksAway 0 when they are up, so callers
 * can distinguish "you're on the clock" from "you're two away" without
 * re-deriving it.
 */
export function nextPickForTeam(
  picks: PickState[],
  currentOverall: number | null,
  teamId: number | null | undefined,
): NextPick | null {
  if (teamId == null || currentOverall == null) return null
  const pick = upcomingPicks(picks, currentOverall).find((p) => p.team_id === teamId)
  if (!pick) return null
  return { pick, picksAway: pick.overall - currentOverall }
}

/** Most recent made picks, newest first. */
export function recentPicks(picks: PickState[], limit: number): PickState[] {
  return picks
    .filter((pick) => pick.status === 'made')
    .sort((a, b) => b.overall - a.overall)
    .slice(0, limit)
}

export interface PoolFilter {
  search: string
  /** 'ALL', or a display position ('QB'..'DST'). */
  position: string
  draftedIds: Set<number | null>
}

/**
 * Search matches name, position or NFL team — the same three fields the old
 * PickForm matched on, so muscle memory from earlier phases still works.
 */
export function matchesSearch(player: PlayerSummary, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return (
    player.full_name.toLowerCase().includes(needle) ||
    displayPosition(player.position).toLowerCase().includes(needle) ||
    (player.nfl_team ?? '').toLowerCase().includes(needle)
  )
}

/**
 * The pool as the list renders it: undrafted, matching the position chip and
 * the search box, still in the server's effective_rank order.
 */
export function filterPool(players: PlayerSummary[], filter: PoolFilter): PlayerSummary[] {
  return players.filter((player) => {
    if (filter.draftedIds.has(player.id)) return false
    if (filter.position !== 'ALL' && displayPosition(player.position) !== filter.position) {
      return false
    }
    return matchesSearch(player, filter.search)
  })
}
